import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridRowModes } from "@mui/x-data-grid";
import {
  TableCell,
  TableSortLabel,
  TableRow,
  Table,
  Paper,
  TableContainer,
  TableHead,
  TableBody,
  TextField,
  Backdrop,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const Verifier = () => {
  const navigate = useNavigate();
  const [applicationData, setApplicationData] = useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [orderBy, setOrderBy] = useState("submitted_at");
  const [searchQuery, setSearchQuery] = useState("");

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredData = applicationData.filter((item) =>
    item.user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const downloadAllApplication = () => {
    setLoading(true);
    const url = window.API_PREFIX + "Admin/api/downloadallapplications";
    const requestOptions = {
      data: sessionStorage.getItem("token"),
    };
    axios
      .post(url, requestOptions)
      .then((res) => {
        if (res.status === 200) {
          const blob = new Blob([res.data], { type: "text/csv" });

          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.setAttribute("download", "all_applications.csv");

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
        } else if (res.status === 401) {
          sessionStorage.clear();
          window.location = "/Expired";
        } else {
          window.location = "/NotAuthorised";
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sortedData = useMemo(
    () =>
      filteredData.sort((a, b) => {
        if (orderBy === "submitted_at") {
          const dateA = a.submitted_at
            ? new Date(a.submitted_at)
            : new Date(a.created_at);
          const dateB = b.submitted_at
            ? new Date(b.submitted_at)
            : new Date(b.created_at);
          return order === "asc" ? dateA - dateB : dateB - dateA;
        }
        return 0;
      }),
    [filteredData, order, orderBy]
  );

  const handleViewClick = (id) => {
    navigate(`/Form/${id}`);
  };

  const handleUnlock = (id) => {
    const url = window.API_PREFIX + "Admin/api/unlockapplication";
    const requestOptions = {
      application_id: id,
      token: sessionStorage.getItem("token"),
    };
    axios
      .post(url, requestOptions)
      .then((res) => {
        if (res.status === 200) {
          message.info("Application unlocked");
        } else if (res.status === 401) {
          sessionStorage.clear();
          window.location = "/Expired";
        } else {
          window.location = "/NotAuthorised";
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);

    const fetchApplications = async () => {
      try {
        const applicationIdSession = sessionStorage.getItem("applicationId");
        if (applicationIdSession) {
          sessionStorage.removeItem("applicationId");
          window.location.reload(true);
          return;
        }

        const url = `${window.API_PREFIX}Admin/api/get/all/application`;
        const requestOptions = {
          data: sessionStorage.getItem("token"),
        };

        const res = await axios.post(url, requestOptions);
        if (res.data.status === 200) {
          setApplicationData(res.data.data);
          setRowModesModel({
            ...rowModesModel,
            id: { mode: GridRowModes.View, ignoreModifications: true },
          });
        } else if (res.data.status === 401) {
          sessionStorage.clear();
          window.location = "/Expired";
          return;
        } else {
          window.location = "/NotAuthorised";
          return;
        }
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const statusMapping = {
    submitted: "Submitted",
    under_process: "Under Process",
    accepted: "Accepted",
    rejected: "Rejected",
  };

  return (
    <div>
      {applicationData.length > 0 ? (
        <>
          <div style={{ width: "80%", margin: "auto", padding: "30px 0" }}>
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search User"
                variant="outlined"
                fullWidth
                margin="normal"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <div
                style={{
                  width: "auto",
                  height: "100%",
                }}
              >
                <Tooltip title="Download all applications">
                  <Button
                    icon={<DownloadOutlined />}
                    type="primary"
                    onClick={downloadAllApplication}
                    size="large"
                  />
                </Tooltip>
              </div>
            </div>
            <TableContainer
              component={Paper}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: ".625rem",
              }}
            >
              <Table aria-label="simple table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>User</b>
                    </TableCell>
                    <TableCell>
                      <b>Application Status</b>
                    </TableCell>
                    <TableCell
                      sortDirection={orderBy === "submitted_at" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "submitted_at"}
                        direction={orderBy === "submitted_at" ? order : "asc"}
                        onClick={() => handleRequestSort("submitted_at")}
                      >
                        <b>Submitted on</b>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <b>Action</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((item, i) => (
                    <TableRow
                      key={i}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {String(item.user_name)}
                      </TableCell>
                      <TableCell
                        style={{
                          textTransform: "capitalize",
                        }}
                      >
                        {statusMapping[item.applicationStatus] || "Pending"}
                      </TableCell>
                      <TableCell
                        style={{
                          textTransform: "capitalize",
                        }}
                      >
                        {item.submitted_at
                          ? dayjs([item.submitted_at]).format("DD-MM-YYYY")
                          : item.created_at
                          ? dayjs(item.created_at).format("DD-MM-YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell
                        style={{
                          display: "flex",
                          gap: ".375rem",
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Open application">
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => handleViewClick(item.id)}
                          >
                            <VisibilityIcon fontSize="medium" />
                          </div>
                        </Tooltip>
                        {item.applicationStatus &&
                          item.applicationStatus === "submitted" && (
                            <Tooltip title="Unlock application">
                              <div
                                style={{ cursor: "pointer" }}
                                onClick={() => handleUnlock(item.id)}
                              >
                                <LockOpenIcon fontSize="medium" />
                              </div>
                            </Tooltip>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>{" "}
        </>
      ) : null}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};
export default Verifier;
